const CREDENTIAL_CONFLICT_CODES = new Set([
  'credential_name_already_exists',
  'auth_webauthn_credential_name_already_exists',
]);

const asText = (value: unknown): string | undefined => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const text = asText(item);
      if (text) {
        return text;
      }
    }
    return undefined;
  }
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    return (
      asText(record.value) ||
      asText(record.title) ||
      asText(record.text) ||
      asText(record.content) ||
      asText(record.en) ||
      asText(record['en-US'])
    );
  }
  return undefined;
};

const unwrapPasskeyError = (error: unknown): Record<string, any> => {
  if (error == null) {
    return {};
  }
  if (typeof error === 'string') {
    try {
      const parsed = JSON.parse(error);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed;
      }
    } catch {
      return { message: error };
    }
    return { message: error };
  }
  if (typeof error === 'object') {
    return error as Record<string, any>;
  }
  return {};
};

export const isPasskeyCredentialConflict = (error: unknown): boolean => {
  const payload = unwrapPasskeyError(error);
  const nested = unwrapPasskeyError(payload.data ?? payload.error);
  const codes = [
    payload.error_code,
    payload.ts_error_code,
    payload.errorCode,
    nested.error_code,
    nested.ts_error_code,
    nested.errorCode,
  ];
  if (codes.some((code) => CREDENTIAL_CONFLICT_CODES.has(String(code ?? '')))) {
    return true;
  }
  if (typeof error === 'string') {
    return [...CREDENTIAL_CONFLICT_CODES].some((code) => error.includes(code));
  }
  return false;
};

export interface PasskeyOtpExitModalCopy {
  title?: string;
  description?: string;
  ok: string;
  cancel?: string;
}

const getOtpFlowModal = (
  rbPasskey: any
): { otpFlow: Record<string, any>; modal: Record<string, any> } | null => {
  const otpFlow = rbPasskey?.otpFlow ?? rbPasskey?.managePasskey?.otpFlow;
  if (!otpFlow || typeof otpFlow !== 'object') {
    return null;
  }
  const modalSource = Array.isArray(otpFlow.modal)
    ? otpFlow.modal[0]
    : otpFlow.modal;
  const modal =
    modalSource && typeof modalSource === 'object' ? modalSource : otpFlow;
  return { otpFlow, modal };
};

export const isPasskeyOtpExitModalEnabled = (rbPasskey: any): boolean => {
  const source = getOtpFlowModal(rbPasskey);
  if (!source) {
    return false;
  }
  return source.modal.exitModal === true || source.otpFlow.exitModal === true;
};

export const getPasskeyOtpExitModalCopy = (
  rbPasskey: any
): PasskeyOtpExitModalCopy | null => {
  const source = getOtpFlowModal(rbPasskey);
  if (!source) {
    return null;
  }
  const { otpFlow, modal } = source;
  const cta = modal.cta ?? otpFlow.cta ?? {};
  const title = asText(modal.title) ?? asText(otpFlow.title);
  const description =
    asText(modal.description) ?? asText(otpFlow.description);
  const ok = asText(cta.ok) ?? asText(cta.OK);
  const cancel = asText(cta.Cancel) ?? asText(cta.cancel);
  if (!ok) {
    return null;
  }
  return { title, description, ok, cancel };
};
